import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Circle, Clock, HelpCircle, ExternalLink, PlayCircle } from 'lucide-react';

interface GuideStep {
  id: string;
  title: string;
  description: string;
  completed?: boolean;
  estimated_time?: string;
  type?: 'action' | 'info' | 'verification';
}

interface HelpGuideData {
  title: string;
  description: string;
  steps: GuideStep[];
  estimated_time: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  category?: string;
  relatedTopics?: string[];
}

interface HelpGuideWidgetProps {
  data?: HelpGuideData | null;
  onAction?: (action: string, data?: any) => void;
}

export const HelpGuideWidget = ({ data, onAction }: HelpGuideWidgetProps) => {
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [currentStep, setCurrentStep] = useState(0);

  // BUG-39: `data` comes from untrusted AI-generated chat payloads (widget: any).
  // A `help-guide` widget can arrive with `data` undefined or `steps` missing —
  // render a graceful fallback instead of crashing the surrounding chat view.
  const steps = data?.steps ?? [];
  const hasGuide = !!data && steps.length > 0;

  const handleAction = (action: string, actionData?: any) => {
    onAction?.(action, actionData);
  };

  const toggleStepCompletion = (stepId: string) => {
    const newCompleted = new Set(completedSteps);
    if (newCompleted.has(stepId)) {
      newCompleted.delete(stepId);
    } else {
      newCompleted.add(stepId);
    }
    setCompletedSteps(newCompleted);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-success-muted text-success';
      case 'intermediate': return 'bg-warning-muted text-warning';
      case 'advanced': return 'bg-destructive/10 text-destructive';
      default: return 'bg-muted text-foreground';
    }
  };

  const getStepTypeIcon = (type: string) => {
    switch (type) {
      case 'action': return <PlayCircle className="h-4 w-4 text-primary" />;
      case 'verification': return <CheckCircle className="h-4 w-4 text-success" />;
      default: return <HelpCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const completionPercentage = steps.length
    ? Math.round((completedSteps.size / steps.length) * 100)
    : 0;

  if (!hasGuide) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <HelpCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm font-medium text-foreground">No guide available</p>
          <p className="text-sm text-muted-foreground mt-1">
            There are no steps to show for this guide right now.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{data.title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{data.description}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {data.difficulty && (
            <Badge className={getDifficultyColor(data.difficulty)}>
              {data.difficulty}
            </Badge>
          )}
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Clock className="h-3 w-3" />
            {data.estimated_time}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Progress</span>
          <span className="font-medium">{completedSteps.size}/{steps.length} completed</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-accent0 h-2 rounded-full transition-all duration-300"
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {steps.map((step, index) => {
          const isCompleted = completedSteps.has(step.id);
          const isCurrent = index === currentStep;
          
          return (
            <Card 
              key={step.id} 
              className={`transition-all ${
                isCurrent ? 'ring-2 ring-ring bg-accent' : 
                isCompleted ? 'bg-success-muted border-success/30' : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => toggleStepCompletion(step.id)}
                    className={`mt-1 transition-colors ${
                      isCompleted ? 'text-success' : 'text-muted-foreground hover:text-muted-foreground'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <Circle className="h-5 w-5" />
                    )}
                  </button>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className={`font-medium ${isCompleted ? 'text-success' : 'text-foreground'}`}>
                        Step {index + 1}: {step.title}
                      </h4>
                      {step.type && getStepTypeIcon(step.type)}
                      {step.estimated_time && (
                        <Badge variant="outline" className="text-xs">
                          {step.estimated_time}
                        </Badge>
                      )}
                    </div>
                    
                    <p className={`text-sm ${isCompleted ? 'text-success' : 'text-muted-foreground'}`}>
                      {step.description}
                    </p>
                    
                    {isCurrent && !isCompleted && (
                      <div className="mt-2 flex gap-2">
                        <Button 
                          size="sm"
                          onClick={() => {
                            toggleStepCompletion(step.id);
                            if (index < steps.length - 1) {
                              setCurrentStep(index + 1);
                            }
                          }}
                        >
                          Complete Step
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleAction('get-help-with-step', step)}
                        >
                          Need Help?
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Related Topics */}
      {data.relatedTopics && data.relatedTopics.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Related Topics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.relatedTopics.map((topic, index) => (
              <button
                key={index}
                onClick={() => handleAction('view-related-topic', topic)}
                className="flex items-center gap-2 text-sm text-primary hover:text-primary hover:bg-accent p-2 rounded-lg transition-colors w-full text-left"
              >
                <ExternalLink className="h-3 w-3" />
                {topic}
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button 
          className="flex-1"
          onClick={() => handleAction('start-guide')}
          disabled={completionPercentage === 100}
        >
          {completionPercentage === 100 ? 'Completed!' : 'Start Guide'}
        </Button>
        <Button 
          variant="outline" 
          className="flex-1"
          onClick={() => handleAction('skip-to-end')}
        >
          Skip to Summary
        </Button>
        <Button 
          variant="outline"
          onClick={() => handleAction('get-more-help')}
        >
          More Help
        </Button>
      </div>

      {/* Zephyr Footer */}
      <div className="bg-accent rounded-lg p-3 border border-border">
        <div className="flex items-start gap-2">
          <div className="w-2 h-2 bg-accent0 rounded-full mt-2 flex-shrink-0"></div>
          <div>
            <p className="text-sm font-medium text-accent-foreground">Zephyr Support</p>
            <p className="text-sm text-accent-foreground">
              {completionPercentage === 100 
                ? "Great job completing this guide! You're ready to move on to more advanced topics."
                : `I'm here to help you through each step. You're ${completionPercentage}% complete - keep going!`
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};